/**
 * NoNextStepCard - the Dashboard's "No next step" list (T-05): every lead
 * (a site or site-less account, Designation Lead, Status Active or Cold) and
 * every open opportunity (status Open, not a won project) with NO open
 * task. It catches what slips past the next-step prompt, including records
 * that were already there before the rule existed.
 *
 * One fetch of open tasks plus the accounts and projects already in
 * AppContext; the rule itself is nextStep.recordsWithoutNextStep, the same
 * one the prompt uses. Hidden when the API refuses (non-BD roles). A re-read
 * that fails otherwise (a 429 or 502 while every open tab re-reads after
 * someone's change) keeps the list as it is.
 *
 * The list itself is exported as NoNextStepGroup so the Leads Dashboard
 * (D-06) draws its Stalled and No next step lists the same way.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { leadHash, opportunityHash, recordsWithoutNextStep } from './nextStep';
import { useTasksChanged } from '../tasks/useTasks';
import { latestReads } from '../../live/useLive';
import { FILL_CARD, FILL_LABEL, FILL_SCROLL } from '../dashboard/cardStyles';

const FONT = 'inherit';
const FIRST = 10;
// the list scrolls inside this height so the Project tasks card below stays in view
const BODY_MAX = 220;

/**
 * One titled list: the first 10 rows, "Show all N", and a click on a row sets
 * window.location.hash = hashOf(row.id). `empty` is the line shown when there
 * are no rows.
 */
function Group({ title, rows, hashOf, detailOf, empty = 'None - every one has an open task.' }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, FIRST);
  return (
    <div>
      <div
        style={{
          padding: '7px 16px',
          background: '#FAFAF7',
          borderBottom: '1px solid #F0F0EC',
          fontSize: 9.5,
          fontWeight: 800,
          color: '#5A5A5A',
          letterSpacing: 0.8,
          textTransform: 'uppercase',
        }}
      >
        {title} <span style={{ color: rows.length ? '#B07503' : '#9AA29B' }}>({rows.length})</span>
      </div>
      {rows.length === 0 && (
        <div style={{ padding: '7px 16px', fontSize: 10.5, color: '#9AA29B', borderBottom: '1px solid #F5F5F2' }}>{empty}</div>
      )}
      {shown.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => {
            window.location.hash = hashOf(r.id);
          }}
          title={`Open ${r.label}`}
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            width: '100%',
            padding: '7px 16px',
            background: '#fff',
            border: 'none',
            borderBottom: '1px solid #F5F5F2',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: FONT,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 10.5,
              fontWeight: 600,
              color: '#0D0D0D',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {r.label}
          </span>
          {detailOf(r)}
        </button>
      ))}
      {rows.length > FIRST && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '6px 16px',
            fontSize: 9.5,
            color: '#1B5E20',
            fontWeight: 700,
            fontFamily: FONT,
            background: 'none',
            border: 'none',
            borderBottom: '1px solid #F5F5F2',
            cursor: 'pointer',
          }}
        >
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

export { Group as NoNextStepGroup };

const tagSt = { fontSize: 9, color: '#888', whiteSpace: 'nowrap', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' };

export default function NoNextStepCard({ fill = false } = {}) {
  const { allAccounts, allProjects, loading, resolveOwnerName } = useApp();
  const [openTasks, setOpenTasks] = useState(null); // null = not loaded / not allowed
  // "Loading…" only until the first load lands - every AppContext refresh flips loading again
  const [loadedOnce, setLoadedOnce] = useState(!loading);
  if (!loading && !loadedOnce) setLoadedOnce(true);
  const firstLoad = loading && !loadedOnce;
  const [reads] = useState(latestReads); // reads can overlap: the newest answer GOT wins
  const load = useCallback(() => {
    const ticket = reads.start();
    fetch('/api/crm-actions?status=open')
      .then(async (r) => {
        if (r.status === 403) {
          if (reads.show(ticket)) setOpenTasks(null);
          return;
        } // the API refuses this role: no card
        const rows = r.ok ? await r.json() : null;
        if (Array.isArray(rows) && reads.show(ticket)) setOpenTasks(rows);
      })
      .catch(() => {
        /* the list stays as it is */
      });
  }, [reads]);
  useEffect(load, [load]);
  useTasksChanged(load);

  const { leads, opportunities } = useMemo(
    () => recordsWithoutNextStep(allAccounts, allProjects, openTasks || []),
    [allAccounts, allProjects, openTasks]
  );
  if (openTasks === null) return null;

  const total = leads.length + opportunities.length;
  const warn = total > 0;
  // task owners can be emails; lead owners are display names already - only an email is resolved
  const ownerName = (o) => (o && resolveOwnerName && String(o).includes('@') ? resolveOwnerName(o) : o) || '';

  return (
    // fill: one card that takes the whole Dashboard widget, its list scrolling inside
    <div style={fill ? { ...FILL_CARD, fontFamily: FONT, border: `1px solid ${warn ? '#FFE0B2' : '#ECECEC'}` } : { marginBottom: 16 }}>
      <div
        style={
          fill
            ? { ...FILL_LABEL, fontFamily: FONT }
            : {
                fontSize: 9,
                fontWeight: 700,
                color: '#5A5A5A',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                marginBottom: 8,
                fontFamily: FONT,
              }
        }
      >
        No next step
      </div>
      <div
        style={
          fill
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: '1px solid #EEE' }
            : {
                background: '#fff',
                border: `1px solid ${warn ? '#FFE0B2' : '#E5E9DD'}`,
                borderRadius: 10,
                overflow: 'hidden',
                fontFamily: FONT,
              }
        }
      >
        <div style={{ padding: '9px 16px', background: warn ? '#FFF8EC' : '#F6F9F3', borderBottom: '1px solid #EEE' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: warn ? '#B07503' : '#1B5E20' }}>
            {firstLoad
              ? 'Loading…'
              : warn
                ? `${leads.length} lead${leads.length === 1 ? '' : 's'} · ${opportunities.length} opportunit${opportunities.length === 1 ? 'y' : 'ies'} with no open task`
                : 'Every lead and open opportunity has a next step'}
          </span>
        </div>
        {!firstLoad && warn && (
          <div role="region" aria-label="Records with no next step" style={fill ? FILL_SCROLL : { maxHeight: BODY_MAX, overflowY: 'auto' }}>
            <Group
              title="Leads"
              rows={leads}
              hashOf={leadHash}
              detailOf={(r) => (
                <>
                  {r.status === 'Cold' && (
                    <span
                      style={{
                        fontSize: 8.5,
                        fontWeight: 800,
                        color: '#0277BD',
                        background: '#E1F5FE',
                        borderRadius: 8,
                        padding: '1px 6px',
                      }}
                    >
                      Cold
                    </span>
                  )}
                  {r.owner && <span style={tagSt}>{ownerName(r.owner)}</span>}
                </>
              )}
            />
            <Group
              title="Opportunities"
              rows={opportunities}
              hashOf={opportunityHash}
              detailOf={(r) => (
                <>
                  {r.customer && <span style={tagSt}>{r.customer}</span>}
                  {r.owner && <span style={tagSt}>{ownerName(r.owner)}</span>}
                </>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
